/**
 * Sõidulehed / Transport Request — full flow.
 *
 * Steps (single page, scrollable):
 *   1. Pick request type
 *   2. Pickup + dropoff address (geocoded via /api/map/geocode)
 *   3. Vehicle requirements (type, payload, 4x4, lift, ADR, …)
 *   4. Route preview (Leaflet, only renders when both points geocoded —
 *      never any "default" location)
 *   5. Submit (POST /api/transport/request) → success card
 *
 * All visible strings come from `t('transportRequest.*')`. No inline
 * `{en,et}` dictionaries.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { MapContainer, TileLayer, Marker, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  Truck, MapPin, Package, Home, ArrowRight, Loader2, CheckCircle2,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';

import { GlobalNav } from '@/components/GlobalNav';
import { useLanguage } from '@/lib/context';
import { usePrefs } from '@/lib/usePrefs';
import BudgetSection, { BudgetSummary } from '@/components/BudgetSection';

const API = process.env.REACT_APP_BACKEND_URL;

// ---- decode polyline (Google's encoded polyline algorithm) ----
function decodePolyline(str) {
  if (!str) return [];
  let i = 0, lat = 0, lng = 0;
  const out = [];
  while (i < str.length) {
    let b, shift = 0, result = 0;
    do { b = str.charCodeAt(i++) - 63; result |= (b & 0x1f) << shift; shift += 5; } while (b >= 0x20);
    const dlat = (result & 1) ? ~(result >> 1) : (result >> 1);
    lat += dlat;
    shift = 0; result = 0;
    do { b = str.charCodeAt(i++) - 63; result |= (b & 0x1f) << shift; shift += 5; } while (b >= 0x20);
    const dlng = (result & 1) ? ~(result >> 1) : (result >> 1);
    lng += dlng;
    out.push([lat / 1e5, lng / 1e5]);
  }
  return out;
}

const REQUEST_TYPES = [
  { id: 'local_transport', icon: Truck, labelKey: 'typeLocal', descKey: 'typeLocalDesc' },
  { id: 'long_distance',   icon: MapPin, labelKey: 'typeLong', descKey: 'typeLongDesc' },
  { id: 'moving',          icon: Home, labelKey: 'typeMoving', descKey: 'typeMovingDesc' },
  { id: 'package',         icon: Package, labelKey: 'typePackage', descKey: 'typePackageDesc' },
  { id: 'logistics',       icon: Truck, labelKey: 'typeLogistics', descKey: 'typeLogisticsDesc' },
];

const VEHICLE_TYPES = [
  'car', 'van', 'truck', 'trailer', 'minibus', 'flatbed', 'tow_truck', 'other',
];

// ---- Auto-fit map bounds when route changes ----
function FitBoundsOnce({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points || points.length < 2) return;
    const bounds = L.latLngBounds(points.map((p) => [p.lat, p.lng]));
    map.fitBounds(bounds.pad(0.25), { animate: true });
  }, [points, map]);
  return null;
}

const pinIcon = new L.DivIcon({
  className: 'transport-pin',
  html: '<div style="width:14px;height:14px;border-radius:50%;background:#f97316;border:3px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.3)"></div>',
  iconSize: [14, 14],
  iconAnchor: [7, 7],
});

const TransportRequestPage = () => {
  const { t } = useLanguage();
  const { prefs } = usePrefs();
  const tx = useCallback((k, fb) => t(`transportRequest.${k}`) || fb || '', [t]);

  const country = prefs?.workCountry || prefs?.serviceCountry || 'NO';
  const lang = prefs?.uiLang || 'en';

  const [requestType, setRequestType] = useState('local_transport');
  const [pickupText, setPickupText] = useState('');
  const [dropoffText, setDropoffText] = useState('');
  const [pickup, setPickup] = useState(null);   // {lat,lng,normalized_address}
  const [dropoff, setDropoff] = useState(null);
  const [routeInfo, setRouteInfo] = useState(null); // {distance_km, duration_min, polyline}
  const [routeLoading, setRouteLoading] = useState(false);
  const [pickupBusy, setPickupBusy] = useState(false);
  const [dropoffBusy, setDropoffBusy] = useState(false);

  const [vehicleType, setVehicleType] = useState('van');
  const [payloadKg, setPayloadKg] = useState('');
  const [volumeM3, setVolumeM3] = useState('');
  const [requires4x4, setRequires4x4] = useState(false);
  const [trailerRequired, setTrailerRequired] = useState(false);
  const [tailLift, setTailLift] = useState(false);
  const [adrRequired, setAdrRequired] = useState(false);
  const [tempControlled, setTempControlled] = useState(false);

  const [cargoDesc, setCargoDesc] = useState('');
  const [helpersNeeded, setHelpersNeeded] = useState(0);
  const [fragile, setFragile] = useState(false);
  const [notes, setNotes] = useState('');

  const [contactName, setContactName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactEmail, setContactEmail] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [createdId, setCreatedId] = useState(null);
  const [recommend, setRecommend] = useState(null);
  const [budget, setBudget] = useState(null);  // BudgetSection

  const polylinePoints = useMemo(
    () => (routeInfo?.polyline ? decodePolyline(routeInfo.polyline) : []),
    [routeInfo],
  );

  // Geocode helpers
  const geocode = async (q) => {
    const r = await axios.post(`${API}/api/map/geocode`, {
      query: q, country, language: lang,
    });
    return r.data;
  };

  const onPickupBlur = async () => {
    const q = pickupText.trim();
    if (!q || pickupBusy) return;
    setPickupBusy(true);
    try {
      const g = await geocode(q);
      setPickup({ address_text: g.normalized_address || q, lat: g.lat, lng: g.lng });
      setPickupText(g.normalized_address || q);
    } catch (e) {
      setPickup(null);
      toast.error(tx('errAddressNotFound', 'Adresse ikke funnet'));
    } finally {
      setPickupBusy(false);
    }
  };

  const onDropoffBlur = async () => {
    const q = dropoffText.trim();
    if (!q || dropoffBusy) return;
    setDropoffBusy(true);
    try {
      const g = await geocode(q);
      setDropoff({ address_text: g.normalized_address || q, lat: g.lat, lng: g.lng });
      setDropoffText(g.normalized_address || q);
    } catch (e) {
      setDropoff(null);
      toast.error(tx('errAddressNotFound', 'Adresse ikke funnet'));
    } finally {
      setDropoffBusy(false);
    }
  };

  // Compute route whenever both points are known
  useEffect(() => {
    if (!pickup || !dropoff) {
      setRouteInfo(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setRouteLoading(true);
      try {
        const r = await axios.post(`${API}/api/map/route`, {
          from: { lat: pickup.lat, lng: pickup.lng },
          to:   { lat: dropoff.lat, lng: dropoff.lng },
          overview: true,
        });
        if (!cancelled) setRouteInfo(r.data);
      } catch (e) {
        if (!cancelled) setRouteInfo(null);
      } finally {
        if (!cancelled) setRouteLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [pickup, dropoff]);

  // Re-run recommendation when key inputs change
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.post(`${API}/api/transport/recommend`, {
          request_type: requestType,
          weight_kg: Number(payloadKg) || 0,
          helpers_needed: Number(helpersNeeded) || 0,
          requires_4x4: requires4x4,
          fragile,
          distance_km: routeInfo?.distance_km || 0,
        });
        if (!cancelled) setRecommend(r.data);
      } catch (_) { /* keep silent — recommendations are advisory */ }
    })();
    return () => { cancelled = true; };
  }, [requestType, payloadKg, helpersNeeded, requires4x4, fragile, routeInfo?.distance_km]);

  const canSubmit = pickup && dropoff && requestType && vehicleType && !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const payload = {
        request_type: requestType,
        pickup, dropoff,
        route: routeInfo
          ? { distance_km: routeInfo.distance_km, duration_min: routeInfo.duration_min, polyline: routeInfo.polyline }
          : null,
        vehicle: {
          vehicle_type: vehicleType,
          payload_kg: Number(payloadKg) || null,
          volume_m3: Number(volumeM3) || null,
          requires_4x4: requires4x4,
          trailer_required: trailerRequired,
          tail_lift_required: tailLift,
          adr_required: adrRequired,
          temperature_controlled: tempControlled,
        },
        cargo: {
          description: cargoDesc,
          weight_kg: Number(payloadKg) || null,
          fragile,
          helpers_needed: Number(helpersNeeded) || 0,
        },
        notes,
        country, lang,
        contact_name: contactName || null,
        contact_phone: contactPhone || null,
        contact_email: contactEmail || null,
        budget,
      };
      const r = await axios.post(`${API}/api/transport/request`, payload);
      setCreatedId(r.data.id);
      toast.success(tx('toastSubmitted', 'Forespørsel sendt'));
    } catch (e) {
      toast.error(tx('toastSubmitFailed', 'Kunne ikke sende forespørselen'));
    } finally {
      setSubmitting(false);
    }
  };

  // Reusable "switch row"
  const SwitchRow = ({ label, checked, onChange, testId }) => (
    <label className="flex items-center justify-between py-2 border-b border-slate-100 last:border-b-0">
      <span className="text-sm text-slate-700">{label}</span>
      <Switch checked={checked} onCheckedChange={onChange} data-testid={testId} />
    </label>
  );

  if (createdId) {
    // Pre-zoom map to the route midpoint so the partner sees demo drivers
    // right around their pickup/drop-off corridor.
    const midLat = pickup && dropoff ? (pickup.lat + dropoff.lat) / 2 : (pickup?.lat ?? null);
    const midLng = pickup && dropoff ? (pickup.lng + dropoff.lng) / 2 : (pickup?.lng ?? null);
    const findDriverHref = (() => {
      const sp = new URLSearchParams({
        demo: '1',
        subtype: 'transport',
        country: country || 'NO',
        lang,
        scope: 'distance',
        radius: '100',
      });
      if (midLat != null && midLng != null) {
        sp.set('lat', String(midLat));
        sp.set('lng', String(midLng));
      }
      return `/map?${sp.toString()}`;
    })();

    return (
      <div className="min-h-screen bg-white">
        <GlobalNav />
        <main className="max-w-2xl mx-auto px-4 py-12 text-center" data-testid="tr-success">
          <CheckCircle2 className="h-14 w-14 text-emerald-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2">
            {tx('successTitle', 'Forespørsel mottatt!')}
          </h1>
          <p className="text-slate-600 mb-6">{tx('successSub', 'Vi sender den til relevante leverandører.')}</p>
          <div className="bg-slate-50 rounded-xl p-4 inline-block text-left mb-6">
            <div className="text-xs text-slate-500">{tx('successId', 'Forespørsels-ID')}</div>
            <div className="font-mono text-sm" data-testid="tr-success-id">{createdId}</div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href={findDriverHref}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="tr-find-driver"
            >
              <Button size="lg" className="bg-orange-500 hover:bg-orange-600 text-white">
                <MapPin className="h-4 w-4 mr-2" />
                {tx('findDriverCta', 'Finn en sjåfør')}
              </Button>
            </a>
            <a href="/" data-testid="tr-go-home">
              <Button size="lg" variant="outline">
                {tx('goHome', 'Tilbake til forsiden')}
              </Button>
            </a>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="transport-request-page">
      <GlobalNav />
      <main className="max-w-5xl mx-auto px-4 py-8 md:py-12 space-y-8">
        <header>
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900" data-testid="tr-hero-title">
            {tx('pageTitle', 'Lag en transportforespørsel')}
          </h1>
          <p className="text-slate-600 mt-2" data-testid="tr-hero-sub">
            {tx('pageSub', 'Velg type, kjøretøy og adresser. Vi finner riktig sjåfør for deg.')}
          </p>
        </header>

        {/* STEP 1 — Request type */}
        <section data-testid="tr-step-type">
          <h2 className="text-sm font-bold uppercase tracking-wide text-slate-700 mb-3">
            {tx('stepTypeTitle', '1. Type forespørsel')}
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
            {REQUEST_TYPES.map((rt) => {
              const Icon = rt.icon;
              const active = requestType === rt.id;
              return (
                <button
                  key={rt.id}
                  type="button"
                  onClick={() => setRequestType(rt.id)}
                  data-testid={`tr-type-${rt.id}`}
                  className={`text-left rounded-xl border p-3 transition ${
                    active ? 'border-orange-500 bg-orange-50' : 'border-slate-200 hover:border-orange-300'
                  }`}
                >
                  <Icon className="h-5 w-5 text-orange-600 mb-1.5" />
                  <div className="font-semibold text-sm text-slate-900">{tx(rt.labelKey)}</div>
                  <div className="text-xs text-slate-600 mt-0.5">{tx(rt.descKey)}</div>
                </button>
              );
            })}
          </div>
        </section>

        {/* STEP 2 — Addresses */}
        <section data-testid="tr-step-addresses">
          <h2 className="text-sm font-bold uppercase tracking-wide text-slate-700 mb-3">
            {tx('stepAddressTitle', '2. Adresser')}
          </h2>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-sm text-slate-700 mb-1 block">{tx('pickupAddress', 'Henteadresse')}</label>
              <Input
                placeholder={tx('pickupPlaceholder', 'Adresse, by')}
                value={pickupText}
                onChange={(e) => setPickupText(e.target.value)}
                onBlur={onPickupBlur}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onPickupBlur(); } }}
                data-testid="tr-pickup-input"
              />
              {pickupBusy && <div className="text-xs text-slate-500 mt-1"><Loader2 className="inline h-3 w-3 animate-spin mr-1"/>{tx('geocoding', 'Søker...')}</div>}
              {pickup && (
                <div className="text-xs text-emerald-700 mt-1" data-testid="tr-pickup-resolved">
                  ✓ {pickup.address_text}
                </div>
              )}
            </div>
            <div>
              <label className="text-sm text-slate-700 mb-1 block">{tx('dropoffAddress', 'Leveringsadresse')}</label>
              <Input
                placeholder={tx('dropoffPlaceholder', 'Adresse, by')}
                value={dropoffText}
                onChange={(e) => setDropoffText(e.target.value)}
                onBlur={onDropoffBlur}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onDropoffBlur(); } }}
                data-testid="tr-dropoff-input"
              />
              {dropoffBusy && <div className="text-xs text-slate-500 mt-1"><Loader2 className="inline h-3 w-3 animate-spin mr-1"/>{tx('geocoding', 'Søker...')}</div>}
              {dropoff && (
                <div className="text-xs text-emerald-700 mt-1" data-testid="tr-dropoff-resolved">
                  ✓ {dropoff.address_text}
                </div>
              )}
            </div>
          </div>
        </section>

        {/* STEP 3 — Vehicle */}
        <section data-testid="tr-step-vehicle">
          <h2 className="text-sm font-bold uppercase tracking-wide text-slate-700 mb-3">
            {tx('stepVehicleTitle', '3. Kjøretøykrav')}
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-3">
              <div>
                <label className="text-sm text-slate-700 mb-1 block">{tx('vehicleType', 'Kjøretøytype')}</label>
                <Select value={vehicleType} onValueChange={setVehicleType}>
                  <SelectTrigger data-testid="tr-vehicle-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {VEHICLE_TYPES.map((v) => (
                      <SelectItem key={v} value={v} data-testid={`tr-veh-${v}`}>
                        {tx(`vehicle_${v}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm text-slate-700 mb-1 block">{tx('payloadKg', 'Vekt (kg)')}</label>
                  <Input type="number" min="0" value={payloadKg} onChange={(e) => setPayloadKg(e.target.value)} data-testid="tr-payload" />
                </div>
                <div>
                  <label className="text-sm text-slate-700 mb-1 block">{tx('volumeM3', 'Volum (m³)')}</label>
                  <Input type="number" min="0" value={volumeM3} onChange={(e) => setVolumeM3(e.target.value)} data-testid="tr-volume" />
                </div>
              </div>
            </div>
            <div className="bg-slate-50 rounded-xl p-3">
              <SwitchRow label={tx('requires4x4', '4x4 kreves')} checked={requires4x4} onChange={setRequires4x4} testId="tr-4x4" />
              <SwitchRow label={tx('trailerReq', 'Tilhenger')} checked={trailerRequired} onChange={setTrailerRequired} testId="tr-trailer" />
              <SwitchRow label={tx('tailLift', 'Bakløfter')} checked={tailLift} onChange={setTailLift} testId="tr-taillift" />
              <SwitchRow label={tx('adr', 'ADR (farlig gods)')} checked={adrRequired} onChange={setAdrRequired} testId="tr-adr" />
              <SwitchRow label={tx('tempControlled', 'Temperaturregulert')} checked={tempControlled} onChange={setTempControlled} testId="tr-temp" />
            </div>
          </div>

          {recommend && (
            <div className="mt-3 bg-blue-50 border border-blue-200 rounded-xl p-3" data-testid="tr-recommend">
              <div className="flex items-center gap-2 mb-2">
                <Badge className="bg-blue-100 text-blue-800 border-0">
                  {tx('recommendBadge', 'Anbefaling')}
                </Badge>
                <span className="text-sm text-blue-900 font-medium">
                  {tx('recommendVehicle', 'Foreslått kjøretøy')}: {tx(`vehicle_${recommend.suggested_vehicle}`)}
                </span>
              </div>
              <ul className="text-sm text-blue-900 space-y-1 ml-1">
                {(recommend.hints || []).map((h, i) => (
                  <li key={i}>• {t(h.key)}</li>
                ))}
              </ul>
            </div>
          )}
        </section>

        {/* STEP 4 — Map preview */}
        <section data-testid="tr-step-map">
          <h2 className="text-sm font-bold uppercase tracking-wide text-slate-700 mb-3">
            {tx('stepMapTitle', '4. Forhåndsvisning av rute')}
          </h2>
          {!pickup || !dropoff ? (
            <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center text-sm text-slate-500" data-testid="tr-map-empty">
              {tx('mapEmptyHint', 'Skriv inn begge adressene for å se ruten på kartet.')}
            </div>
          ) : (
            <div className="rounded-xl overflow-hidden border border-slate-200" style={{ height: 360 }}>
              <MapContainer
                center={[(pickup.lat + dropoff.lat) / 2, (pickup.lng + dropoff.lng) / 2]}
                zoom={9}
                scrollWheelZoom={false}
                style={{ height: '100%', width: '100%' }}
                data-testid="tr-map-container"
              >
                <TileLayer
                  attribution='&copy; OpenStreetMap'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <Marker position={[pickup.lat, pickup.lng]} icon={pinIcon} />
                <Marker position={[dropoff.lat, dropoff.lng]} icon={pinIcon} />
                {polylinePoints.length > 1 && (
                  <Polyline positions={polylinePoints} pathOptions={{ color: '#f97316', weight: 4, opacity: 0.85 }} />
                )}
                <FitBoundsOnce points={[pickup, dropoff]} />
              </MapContainer>
            </div>
          )}
          {(routeLoading || routeInfo) && (
            <div className="mt-3 flex items-center gap-4 text-sm" data-testid="tr-route-info">
              {routeLoading && <span className="text-slate-500"><Loader2 className="inline h-3 w-3 animate-spin mr-1"/>{tx('routing', 'Beregner ruten...')}</span>}
              {routeInfo && (
                <>
                  <span><strong>{routeInfo.distance_km} km</strong></span>
                  <span><strong>{Math.round(routeInfo.duration_min)} min</strong></span>
                </>
              )}
            </div>
          )}
        </section>

        {/* STEP 5 — Cargo + contact */}
        <section data-testid="tr-step-cargo">
          <h2 className="text-sm font-bold uppercase tracking-wide text-slate-700 mb-3">
            {tx('stepCargoTitle', '5. Last og kontaktinfo')}
          </h2>
          <div className="grid md:grid-cols-2 gap-3">
            <Textarea
              placeholder={tx('cargoDescPlaceholder', 'Beskriv lasten kort')}
              value={cargoDesc}
              onChange={(e) => setCargoDesc(e.target.value)}
              rows={3}
              data-testid="tr-cargo-desc"
            />
            <div className="space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs text-slate-600">{tx('helpers', 'Hjelpere')}</label>
                  <Input type="number" min="0" value={helpersNeeded} onChange={(e) => setHelpersNeeded(e.target.value)} data-testid="tr-helpers" />
                </div>
                <label className="flex items-center gap-2 mt-5">
                  <Switch checked={fragile} onCheckedChange={setFragile} data-testid="tr-fragile" />
                  <span className="text-sm text-slate-700">{tx('fragile', 'Skjør last')}</span>
                </label>
              </div>
              <Input placeholder={tx('contactName', 'Navn (valgfritt)')} value={contactName} onChange={(e) => setContactName(e.target.value)} data-testid="tr-contact-name" />
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder={tx('contactPhone', 'Telefon')} value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} data-testid="tr-contact-phone" />
                <Input placeholder={tx('contactEmail', 'E-post')} value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} data-testid="tr-contact-email" />
              </div>
            </div>
          </div>
          <Textarea
            placeholder={tx('notesPlaceholder', 'Tilleggsnotater')}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            className="mt-3"
            data-testid="tr-notes"
          />
        </section>

        <section data-testid="tr-step-budget" className="rounded-xl border border-slate-200 p-4 bg-white">
          <BudgetSection
            category="transport"
            country={country}
            value={budget}
            onChange={setBudget}
          />
        </section>

        <div className="flex items-center justify-end gap-3 sticky bottom-3 bg-white/95 backdrop-blur py-3 rounded-xl">
          {!canSubmit && (
            <span className="text-xs text-slate-500" data-testid="tr-submit-hint">
              {tx('submitHint', 'Fyll ut begge adressene for å sende.')}
            </span>
          )}
          <Button
            size="lg"
            disabled={!canSubmit}
            onClick={submit}
            className="bg-orange-500 hover:bg-orange-600 text-white"
            data-testid="tr-submit"
          >
            {submitting
              ? <><Loader2 className="h-4 w-4 mr-2 animate-spin"/>{tx('submitting', 'Sender…')}</>
              : <>{tx('submitCta', 'Send forespørsel')}<ArrowRight className="h-4 w-4 ml-2"/></>
            }
          </Button>
        </div>
      </main>
    </div>
  );
};

export default TransportRequestPage;
export { TransportRequestPage };
